// src/cockpit4/components/Customer360.tsx
// ============================================
// P0-功能3：客户 360° 画像（客户中心）
//
// 功能：
//   1. 顶部：客户基本信息 + 价值评分 + 标签
//   2. 中部：Tab 切换（详情/行为/交易/跟进/文档）
//   3. 底部：时间轴展示关键事件（解析 history 中的日期条目）
//   4. RFM 价值评分（S/A/B/C/D 五级，基于客户数据计算）
//   5. 自动标签 + 手动标签
//   6. 客户生命周期阶段识别（新客户/活跃/沉默/流失/成交）
//   7. 支持搜索选择客户
//
// 数据来源：
//   - RuntimeManager.query(RuntimeName.CRM, { type: "list" })
//   - 真实 CRM 客户档案（history/deals/contact/followup 等字段）
//   - 无数据时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Radar,
  Search,
  RefreshCw,
  Loader2,
  Users,
  Phone,
  Mail,
  MessageSquare,
  MapPin,
  Calendar,
  DollarSign,
  Tag,
  Clock,
  FileText,
  Activity,
  ChevronRight,
  Building2,
  Send,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { useUserStore } from "../../stores/userStore";
import {
  CUSTOMER_CATEGORY_CONFIG,
} from "../../runtime/CRMRuntime/types";
import type { CustomerProfile } from "../../runtime/CRMRuntime/types";

// ============================================
// 类型与常量
// ============================================

const DAY_MS = 24 * 60 * 60 * 1000;

/** RFM 价值等级 */
type RfmLevel = "S" | "A" | "B" | "C" | "D";

/** 生命周期阶段 */
type LifecycleStage = "new" | "active" | "silent" | "churned" | "closed";

/** 详情 Tab */
type DetailTab = "detail" | "behavior" | "transaction" | "followup" | "docs" | "handover";

/** Vault 中读到的 FDE 移交手册 */
interface HandoverDoc {
  path: string;
  name: string;
  content: string;
}

/** 从 markdown frontmatter 取字段（形如 `customer: xxx`） */
function parseFrontmatterField(content: string, field: string): string {
  const m = content.match(new RegExp(`^${field}:\\s*(.+)$`, "m"));
  return m ? m[1].trim() : "";
}

interface RFM {
  r: number; // Recency：距今天数（越小越好）
  f: number; // Frequency：沟通次数
  m: number; // Monetary：金额
  level: RfmLevel;
  score: number; // 0-100 综合分
}

interface TimelineEvent {
  date: string;
  content: string;
}

const RFM_LEVEL_COLOR: Record<RfmLevel, string> = {
  S: "#A855F7",
  A: "#EC4899",
  B: "#3B82F6",
  C: "#6B7280",
  D: "#4B5563",
};

const LIFECYCLE_META: Record<LifecycleStage, { label: string; color: string }> = {
  new: { label: "新客户", color: "#60A5FA" },
  active: { label: "活跃", color: "#34D399" },
  silent: { label: "沉默", color: "#FBBF24" },
  churned: { label: "流失", color: "#F87171" },
  closed: { label: "已成交", color: "#A855F7" },
};

const TAB_META: { id: DetailTab; label: string; icon: typeof FileText }[] = [
  { id: "detail", label: "详情", icon: FileText },
  { id: "behavior", label: "行为", icon: Activity },
  { id: "transaction", label: "交易", icon: DollarSign },
  { id: "followup", label: "跟进", icon: Calendar },
  { id: "docs", label: "文档", icon: FileText },
  { id: "handover", label: "移交", icon: Send },
];

// ============================================
// 工具函数
// ============================================

/** 计算距今天数（无效日期返回 -1） */
function daysSince(dateStr: string): number {
  if (!dateStr) return -1;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return -1;
  return Math.floor((Date.now() - t) / DAY_MS);
}

/**
 * 解析 history 中的沟通时间线（格式：- YYYY-MM-DD: 内容）
 */
function parseTimeline(history: string): TimelineEvent[] {
  if (!history) return [];
  const events: TimelineEvent[] = [];
  const lines = history.split("\n");
  const re = /^-\s*(\d{4}-\d{2}-\d{2})\s*:\s*(.+)$/;
  for (const line of lines) {
    const m = line.match(re);
    if (m) {
      events.push({ date: m[1], content: m[2].trim() });
    }
  }
  // 按日期降序（最新在前）
  return events.sort((a, b) => (a.date < b.date ? 1 : -1));
}

/** 统计 history 中的沟通次数（用于 F 值） */
function countFollowups(history: string): number {
  if (!history) return 0;
  const matches = history.match(/-\s*\d{4}-\d{2}-\d{2}\s*:/g);
  return matches ? matches.length : 0;
}

/**
 * RFM 价值评分
 * - R：距今天数（越小越好）
 * - F：沟通次数（越多越好）
 * - M：金额（越高越好）
 * 等级规则（满足任一最高级即可）：
 *   S：R<7 且 F>=5 且 M>=100000
 *   A：R<14 且 F>=3 且 M>=50000
 *   B：R<30 且 F>=2 且 M>=10000
 *   C：R<60 且 F>=1 且 M>0
 *   D：其余
 */
function computeRFM(c: CustomerProfile): RFM {
  const r = daysSince(c.followup?.lastContact || "");
  const f = countFollowups(c.history);
  const m = c.followup?.estimatedValue || 0;

  // 综合分（0-100）：R 40% + F 30% + M 30%
  const rScore = r < 0 ? 0 : Math.max(0, 100 - r * 2); // 50 天外归零
  const fScore = Math.min(100, f * 20); // 5 次满分
  const mScore = Math.min(100, Math.round((m / 100000) * 100));
  const score = Math.round(rScore * 0.4 + fScore * 0.3 + mScore * 0.3);

  let level: RfmLevel = "D";
  if (r >= 0 && r < 7 && f >= 5 && m >= 100000) level = "S";
  else if (r >= 0 && r < 14 && f >= 3 && m >= 50000) level = "A";
  else if (r >= 0 && r < 30 && f >= 2 && m >= 10000) level = "B";
  else if (r >= 0 && r < 60 && f >= 1 && m > 0) level = "C";

  return { r: r < 0 ? 0 : r, f, m, level, score };
}

/**
 * 客户生命周期阶段识别
 * - closed-won / active → closed（已成交）
 * - closed-lost → churned（流失）
 * - 其余按 lastContact 判断：
 *   · 7 天内 → active
 *   · 30 天内 → new（新客户，初次接触不久）
 *   · 更久 → silent（沉默）
 */
function computeLifecycle(c: CustomerProfile): LifecycleStage {
  const status = c.followup?.status;
  if (status === "closed-won" || status === "active") return "closed";
  if (status === "closed-lost") return "churned";
  const d = daysSince(c.followup?.lastContact || "");
  if (d < 0) return "new";
  if (d <= 7) return "active";
  if (d <= 30) return "new";
  return "silent";
}

/**
 * 自动标签：基于客户字段推断标签
 */
function autoTags(c: CustomerProfile, rfm: RFM, lifecycle: LifecycleStage): string[] {
  const tags: string[] = [];
  // RFM 等级
  tags.push(`RFM-${rfm.level}`);
  // 生命周期
  tags.push(LIFECYCLE_META[lifecycle].label);
  // 高价值
  if (rfm.m >= 100000) tags.push("高价值");
  else if (rfm.m >= 10000) tags.push("中价值");
  // 快成交
  if (c.followup?.status === "negotiating") tags.push("快成交");
  // 长期客户
  if (rfm.f >= 5) tags.push("高频沟通");
  // 行业标签
  if (c.industry) tags.push(c.industry);
  return tags;
}

/** 格式化金额 */
function formatAmount(amount: number): string {
  if (!amount || amount <= 0) return "¥0";
  if (amount >= 10000) return `¥${(amount / 10000).toFixed(1)}w`;
  return `¥${amount.toLocaleString()}`;
}

// ============================================
// 主组件
// ============================================

export default function Customer360() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>("detail");

  // 加载真实客户列表
  const loadCustomers = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" });
      if (resp?.success && resp.data) {
        const inner = resp.data as { data?: CustomerProfile[] } | CustomerProfile[];
        const list = Array.isArray(inner) ? inner : inner?.data || [];
        setCustomers(Array.isArray(list) ? list : []);
      } else {
        setError(resp?.error || "查询客户列表失败");
      }
    } catch (err) {
      setError((err as Error).message || "加载客户列表异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadCustomers();
  }, [loadCustomers]);

  // 搜索过滤
  const filteredCustomers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      [c.company, c.industry, c.region, c.owner, c.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [customers, searchQuery]);

  // 默认选中第一个客户
  useEffect(() => {
    if (!selectedId && filteredCustomers.length > 0) {
      setSelectedId(filteredCustomers[0].id);
    }
    if (selectedId && !filteredCustomers.some((c) => c.id === selectedId)) {
      setSelectedId(filteredCustomers[0]?.id || null);
    }
  }, [filteredCustomers, selectedId]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === selectedId) || null,
    [customers, selectedId]
  );

  // 选中客户的派生数据
  const derived = useMemo(() => {
    if (!selectedCustomer) return null;
    const rfm = computeRFM(selectedCustomer);
    const lifecycle = computeLifecycle(selectedCustomer);
    const auto = autoTags(selectedCustomer, rfm, lifecycle);
    const timeline = parseTimeline(selectedCustomer.history);
    return { rfm, lifecycle, auto, timeline };
  }, [selectedCustomer]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-purple-500/15 border border-purple-500/30">
            <Radar size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">客户 360° 画像</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Customer 360 · {customers.length} 客户 · 搜索选择查看
            </p>
          </div>
        </div>
        <button
          onClick={loadCustomers}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
        >
          {refreshing ? (
            <Loader2 size={11} className="text-purple-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-purple-400" />
          )}
          <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
            {refreshing ? "刷新中..." : "刷新"}
          </span>
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-purple-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载客户数据...</p>
        </div>
      ) : customers.length === 0 ? (
        <div className="py-10 text-center">
          <Users size={24} className="mx-auto text-gray-600 mb-2" />
          <p className="text-[11px] font-mono text-gray-500">暂无客户数据</p>
          <p className="text-[9px] font-mono text-gray-700 mt-1">
            数据来源：RuntimeManager.query(CRM, {`{ type: "list" }`})
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-3 p-4">
          {/* 左侧：客户搜索 + 列表 */}
          <div className="col-span-4 space-y-2">
            <div className="relative">
              <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-600" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索公司 / 行业 / 负责人..."
                className="w-full pl-7 pr-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-purple-500/40"
              />
            </div>
            <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1">
              {filteredCustomers.length === 0 ? (
                <p className="text-[10px] font-mono text-gray-600 text-center py-3">
                  未匹配到客户
                </p>
              ) : (
                filteredCustomers.map((c) => {
                  const rfm = computeRFM(c);
                  const lifecycle = computeLifecycle(c);
                  const catConfig = CUSTOMER_CATEGORY_CONFIG[c.category] || CUSTOMER_CATEGORY_CONFIG.other;
                  const isActive = c.id === selectedId;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      className={`w-full text-left p-2 rounded-md border transition-all ${
                        isActive
                          ? "border-purple-500/40 bg-purple-500/10"
                          : "border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04]"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[11px] text-gray-200 truncate flex-1">{c.company}</span>
                        <span
                          className="px-1 rounded text-[8px] font-mono font-bold flex-shrink-0"
                          style={{
                            color: RFM_LEVEL_COLOR[rfm.level],
                            background: `${RFM_LEVEL_COLOR[rfm.level]}20`,
                          }}
                        >
                          {rfm.level}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[9px] font-mono text-gray-600">
                        <span
                          className="w-1 h-1 rounded-full"
                          style={{ background: catConfig.color }}
                        />
                        <span style={{ color: catConfig.color }}>{catConfig.label}</span>
                        <span>·</span>
                        <span style={{ color: LIFECYCLE_META[lifecycle].color }}>
                          {LIFECYCLE_META[lifecycle].label}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* 右侧：客户 360 画像 */}
          <div className="col-span-8">
            {!selectedCustomer || !derived ? (
              <div className="h-full flex items-center justify-center text-gray-600">
                <p className="text-[11px] font-mono">请从左侧选择一位客户</p>
              </div>
            ) : (
              <CustomerDetail
                customer={selectedCustomer}
                rfm={derived.rfm}
                lifecycle={derived.lifecycle}
                autoTags={derived.auto}
                timeline={derived.timeline}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ============================================
// 客户详情（右侧）
// ============================================
interface CustomerDetailProps {
  customer: CustomerProfile;
  rfm: RFM;
  lifecycle: LifecycleStage;
  autoTags: string[];
  timeline: TimelineEvent[];
  activeTab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
}

function CustomerDetail({
  customer,
  rfm,
  lifecycle,
  autoTags,
  timeline,
  activeTab,
  onTabChange,
}: CustomerDetailProps) {
  const catConfig = CUSTOMER_CATEGORY_CONFIG[customer.category] || CUSTOMER_CATEGORY_CONFIG.other;
  const lifecycleMeta = LIFECYCLE_META[lifecycle];
  const manualTags = customer.tags || [];

  // 移交 / 自运营：仅 super_admin 可从 Vault 读取 FDE 交付手册
  const isAdmin = useUserStore((s) => s.isSuperAdmin());
  const [handoverDocs, setHandoverDocs] = useState<HandoverDoc[]>([]);
  const [handoverLoading, setHandoverLoading] = useState(false);
  const [expandedPath, setExpandedPath] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab !== "handover" || !isAdmin) {
      if (handoverDocs.length) setHandoverDocs([]);
      return;
    }
    let cancelled = false;
    setHandoverLoading(true);
    (async () => {
      try {
        const listRes = await fetch("/vault-api/list?dir=XEAOS/Finance/FDE&ext=.md").catch(() => null);
        if (!listRes || !listRes.ok) {
          if (!cancelled) setHandoverDocs([]);
          return;
        }
        const listData = await listRes.json();
        const files: { path: string; name: string }[] = listData?.files || [];
        const nameKey = (customer.contact.name || "").trim();
        // 按当前客户名过滤（文件名/路径含客户名），未填名则全量返回
        const matched = files.filter((f) => !nameKey || f.path.includes(nameKey) || f.name.includes(nameKey));
        const out: HandoverDoc[] = [];
        for (const f of matched.slice(0, 20)) {
          const r = await fetch(`/vault-api/read?path=${encodeURIComponent(f.path)}`).catch(() => null);
          if (r && r.ok) {
            const j = await r.json();
            if (j?.content) out.push({ path: f.path, name: f.name, content: j.content as string });
          }
        }
        if (!cancelled) setHandoverDocs(out);
      } catch {
        if (!cancelled) setHandoverDocs([]);
      } finally {
        if (!cancelled) setHandoverLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin, customer.contact.name]);

  // 交易相关：解析 deals 章节
  const dealLines = useMemo(() => {
    if (!customer.deals) return [];
    return customer.deals
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
  }, [customer.deals]);

  // 文档相关：filePath + analysis + suggestion
  const docSections = useMemo(
    () => [
      { key: "analysis", label: "AI 分析", content: customer.analysis },
      { key: "suggestion", label: "下一步建议", content: customer.suggestion },
    ],
    [customer.analysis, customer.suggestion]
  );

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      {/* 顶部：基本信息 + 价值评分 */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${catConfig.color}20`, border: `1px solid ${catConfig.color}40` }}
          >
            <Building2 size={18} style={{ color: catConfig.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-[15px] font-semibold text-white truncate">{customer.company}</h4>
            <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] font-mono">
              <span
                className="px-1.5 py-0.5 rounded"
                style={{ background: `${catConfig.color}20`, color: catConfig.color }}
              >
                {catConfig.label}
              </span>
              <span
                className="px-1.5 py-0.5 rounded"
                style={{ background: `${lifecycleMeta.color}20`, color: lifecycleMeta.color }}
              >
                {lifecycleMeta.label}
              </span>
              <span className="text-gray-500">{customer.industry || "—"}</span>
              <span className="flex items-center gap-0.5 text-gray-500">
                <MapPin size={9} /> {customer.region || "—"}
              </span>
              <span className="flex items-center gap-0.5 text-gray-500">
                <Users size={9} /> {customer.owner || "—"}
              </span>
            </div>
          </div>
          {/* RFM 价值评分 */}
          <div className="flex-shrink-0 text-center">
            <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              RFM 评分
            </div>
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-[20px] font-mono font-bold"
              style={{
                color: RFM_LEVEL_COLOR[rfm.level],
                background: `${RFM_LEVEL_COLOR[rfm.level]}15`,
                border: `1px solid ${RFM_LEVEL_COLOR[rfm.level]}40`,
              }}
            >
              {rfm.level}
            </div>
            <div className="text-[9px] font-mono mt-1" style={{ color: RFM_LEVEL_COLOR[rfm.level] }}>
              {rfm.score}/100
            </div>
          </div>
        </div>

        {/* RFM 三维细分 */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <RfmMetric label="Recency" value={rfm.r === 0 ? "今日" : `${rfm.r}天前`} color="#60A5FA" />
          <RfmMetric label="Frequency" value={`${rfm.f} 次`} color="#34D399" />
          <RfmMetric label="Monetary" value={formatAmount(rfm.m)} color="#FBBF24" />
        </div>

        {/* 标签：自动 + 手动 */}
        <div className="mt-3 flex items-start gap-2">
          <Tag size={11} className="text-gray-500 mt-1 flex-shrink-0" />
          <div className="flex flex-wrap gap-1">
            {autoTags.map((t, i) => (
              <span
                key={`auto-${i}`}
                className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-purple-500/10 text-purple-400 border border-purple-500/20"
                title="自动标签"
              >
                {t}
              </span>
            ))}
            {manualTags.map((t, i) => (
              <span
                key={`manual-${i}`}
                className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-blue-500/10 text-blue-400 border border-blue-500/20"
                title="手动标签"
              >
                {t}
              </span>
            ))}
            {autoTags.length === 0 && manualTags.length === 0 && (
              <span className="text-[10px] font-mono text-gray-600">暂无标签</span>
            )}
          </div>
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-white/[0.06] bg-white/[0.01]">
        {TAB_META.filter((tab) => tab.id !== "handover" || isAdmin).map((tab) => {
          const TIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                isActive
                  ? "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                  : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <TIcon size={10} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab 内容 */}
      <div className="p-4 max-h-[440px] overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            {/* 详情 Tab */}
            {activeTab === "detail" && (
              <div className="grid grid-cols-2 gap-3">
                <DetailBlock title="联系人信息" icon={Users}>
                  <DetailRow icon={Users} label="姓名" value={customer.contact.name} />
                  <DetailRow icon={Phone} label="电话" value={customer.contact.phone} />
                  <DetailRow icon={MessageSquare} label="微信" value={customer.contact.wechat} />
                  <DetailRow icon={Mail} label="邮箱" value={customer.contact.email} />
                </DetailBlock>
                <DetailBlock title="跟进信息" icon={Calendar}>
                  <DetailRow icon={Calendar} label="上次联系" value={customer.followup.lastContact} />
                  <DetailRow icon={Calendar} label="下次联系" value={customer.followup.nextContact} />
                  <DetailRow
                    icon={DollarSign}
                    label="预估金额"
                    value={
                      customer.followup.estimatedValue > 0
                        ? `¥${customer.followup.estimatedValue.toLocaleString()}`
                        : "—"
                    }
                    valueColor="#34D399"
                  />
                  <DetailRow icon={Users} label="负责人" value={customer.owner} />
                </DetailBlock>
              </div>
            )}

            {/* 行为 Tab：沟通时间线 */}
            {activeTab === "behavior" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                    沟通时间线（{timeline.length} 条）
                  </span>
                </div>
                {timeline.length === 0 ? (
                  <p className="text-[11px] font-mono text-gray-600 text-center py-3">
                    暂无行为记录（history 中无日期条目）
                  </p>
                ) : (
                  <Timeline events={timeline} />
                )}
              </div>
            )}

            {/* 交易 Tab：成交记录 + RFM */}
            {activeTab === "transaction" && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <RfmMetric label="Recency" value={rfm.r === 0 ? "今日" : `${rfm.r}天前`} color="#60A5FA" />
                  <RfmMetric label="Frequency" value={`${rfm.f} 次`} color="#34D399" />
                  <RfmMetric label="Monetary" value={formatAmount(rfm.m)} color="#FBBF24" />
                </div>
                <div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-2">
                    成交记录（{dealLines.length} 条）
                  </div>
                  {dealLines.length === 0 ? (
                    <p className="text-[11px] font-mono text-gray-600 text-center py-3">
                      暂无成交记录
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {dealLines.map((line, i) => (
                        <div
                          key={i}
                          className="text-[11px] text-gray-300 leading-snug px-2 py-1 rounded bg-white/[0.02] border border-white/[0.04]"
                        >
                          {line}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 跟进 Tab：history 全文 + 下次跟进 */}
            {activeTab === "followup" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2">
                    <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                      上次联系
                    </div>
                    <div className="text-[12px] font-mono text-gray-200 mt-0.5">
                      {customer.followup.lastContact || "—"}
                    </div>
                  </div>
                  <div className="rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2">
                    <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                      下次联系
                    </div>
                    <div className="text-[12px] font-mono text-gray-200 mt-0.5">
                      {customer.followup.nextContact || "—"}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-2">
                    历史沟通记录
                  </div>
                  <div className="text-[11px] text-gray-300 leading-relaxed whitespace-pre-wrap px-2 py-1 rounded bg-white/[0.02] border border-white/[0.04]">
                    {customer.history || "暂无记录"}
                  </div>
                </div>
              </div>
            )}

            {/* 文档 Tab：AI 分析 + 下一步建议 + 文件路径 */}
            {activeTab === "docs" && (
              <div className="space-y-3">
                {docSections.map((s) => (
                  <div key={s.key}>
                    <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-2">
                      {s.label}
                    </div>
                    <div className="text-[11px] text-gray-300 leading-relaxed whitespace-pre-wrap px-2 py-1 rounded bg-white/[0.02] border border-white/[0.04]">
                      {s.content || "暂无记录"}
                    </div>
                  </div>
                ))}
                <div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-2">
                    档案路径
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 px-2 py-1 rounded bg-white/[0.02] border border-white/[0.04] break-all">
                    {customer.filePath}
                  </div>
                </div>
              </div>
            )}

            {/* 移交 Tab：Vault 中已生成的 FDE 交付自运营手册（仅 super_admin） */}
            {activeTab === "handover" && isAdmin && (
              <div className="space-y-3">
                <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-2">
                  FDE 交付自运营手册（{handoverDocs.length} 份 · 写回 Vault XEAOS/Finance/FDE）
                </div>
                {handoverLoading ? (
                  <p className="text-[11px] font-mono text-gray-600 text-center py-3">加载中…</p>
                ) : handoverDocs.length === 0 ? (
                  <p className="text-[11px] font-mono text-gray-600 text-center py-3">
                    暂无移交手册 · 在 GEO+DSO「交付自转」勾选清单并写回 Vault 后，即在此展示
                  </p>
                ) : (
                  handoverDocs.map((d) => {
                    const cust = parseFrontmatterField(d.content, "customer") || d.name;
                    const type = parseFrontmatterField(d.content, "type") || "移交手册";
                    const dueDate = parseFrontmatterField(d.content, "date") || "";
                    const expanded = expandedPath === d.path;
                    const doneCount = (d.content.match(/\[x\]/g) || []).length;
                    const totalCount = (d.content.match(/\[[ x]\]/g) || []).length;
                    return (
                      <div key={d.path} className="rounded-md border border-white/[0.06] bg-white/[0.02]">
                        <button
                          onClick={() => setExpandedPath(expanded ? null : d.path)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-white/[0.02]"
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            <Send size={12} className="text-gray-500 flex-shrink-0" />
                            <span className="text-[12px] text-gray-200 truncate">{cust}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-[9px] font-mono text-gray-500">
                              {type}{dueDate ? ` · ${dueDate}` : ""}
                              {totalCount > 0 ? ` · ${doneCount}/${totalCount}` : ""}
                            </span>
                            <ChevronRight
                              size={12}
                              className={`text-gray-500 transition-transform ${expanded ? "rotate-90" : ""}`}
                            />
                          </div>
                        </button>
                        {expanded && (
                          <pre className="text-[10px] font-mono text-gray-400 whitespace-pre-wrap break-all px-3 pb-3 max-h-[260px] overflow-y-auto">
                            {d.content}
                          </pre>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* 底部：关键事件时间轴（始终展示，强化 360 视角） */}
      <div className="p-4 border-t border-white/[0.06] bg-white/[0.01]">
        <div className="flex items-center gap-2 mb-2">
          <Clock size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
            关键事件时间轴（{timeline.length}）
          </span>
        </div>
        {timeline.length === 0 ? (
          <p className="text-[10px] font-mono text-gray-600 text-center py-2">
            暂无时间轴事件
          </p>
        ) : (
          <div className="space-y-1 max-h-[160px] overflow-y-auto pr-1">
            {timeline.slice(0, 10).map((e, i) => (
              <div
                key={`${e.date}-${i}`}
                className="flex items-start gap-2 px-2 py-1.5 rounded bg-white/[0.02] border border-white/[0.04]"
              >
                <div className="flex-shrink-0 w-2 h-2 rounded-full bg-cyan-400 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono text-cyan-400">{e.date}</div>
                  <div className="text-[11px] text-gray-300 mt-0.5 leading-snug">
                    {e.content}
                  </div>
                </div>
                <ChevronRight size={10} className="text-gray-600 flex-shrink-0 mt-1" />
              </div>
            ))}
            {timeline.length > 10 && (
              <p className="text-[9px] font-mono text-gray-600 text-center pt-1">
                + {timeline.length - 10} 条更早事件（见"行为" Tab）
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// RFM 指标小卡片
// ============================================
interface RfmMetricProps {
  label: string;
  value: string;
  color: string;
}

function RfmMetric({ label, value, color }: RfmMetricProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">
        {label}
      </div>
      <div className="text-[12px] font-mono font-semibold truncate" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

// ============================================
// 详情块 / 详情行
// ============================================
interface DetailBlockProps {
  title: string;
  icon: typeof Users;
  children: ReactNode;
}

function DetailBlock({ title, icon: Icon, children }: DetailBlockProps) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={12} className="text-gray-400" />
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
          {title}
        </span>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

interface DetailRowProps {
  icon: typeof Users;
  label: string;
  value: string;
  valueColor?: string;
}

function DetailRow({ icon: Icon, label, value, valueColor }: DetailRowProps) {
  return (
    <div className="flex items-center gap-2 text-[11px] font-mono">
      <Icon size={11} className="text-gray-500 flex-shrink-0" />
      <span className="text-gray-500 w-16 flex-shrink-0">{label}：</span>
      <span className="text-gray-300 truncate" style={valueColor ? { color: valueColor } : undefined}>
        {value || "—"}
      </span>
    </div>
  );
}

// ============================================
// 时间轴组件
// ============================================
interface TimelineProps {
  events: TimelineEvent[];
}

function Timeline({ events }: TimelineProps) {
  return (
    <div className="relative pl-4">
      {/* 竖直主线 */}
      <div className="absolute left-[5px] top-1 bottom-1 w-px bg-white/[0.08]" />
      <div className="space-y-2">
        {events.map((e, i) => (
          <motion.div
            key={`${e.date}-${i}`}
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.03 }}
            className="relative"
          >
            <div className="absolute -left-4 top-1.5 w-2 h-2 rounded-full bg-cyan-400 ring-2 ring-cyan-400/20" />
            <div className="pl-2">
              <div className="text-[10px] font-mono text-cyan-400">{e.date}</div>
              <div className="text-[11px] text-gray-300 mt-0.5 leading-snug">
                {e.content}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
